import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { CameraIcon, PencilIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

import { IVehicle } from "../../../models/Vehicle";
import formatDateTime from "../../../utils/formatDateTime";
import PriceFormat from "../../../utils/priceFormat";
import Card from "../../shared/Card/Card";
import VehicleIcons from "../../shared/VehicleIcons/VehicleIcons";
import Modal from "../../../layouts/Modal";
import { useEffect, useState } from "react";
import useVehiclesMutations from "../../../data/useVehiclesMutations";
import useVehicleLookupMode from "../../../hooks/useVehicleLookupMode";
import { identifierMaxLength, identifierValidationMessage, isValidIdentifier, normaliseIdentifier, vehicleIdentifierFor } from "../../../utils/vehicleIdentifier";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import useNotification from "../../notifications/useNotifications";
import { stepSaveErrorReason } from "../../shared/SystemStepField";
import useLeadByVehicleId from "../../../data/useLeadByVehicleId";
import useStockNumber from "../../../hooks/useStockNumber";
import VehicleCollectionQuestions from "../../vehicles/VehicleCollectionQuestions";
import ExportOffersTableRow from "../../exports/ExportOffersTableRow";
import PartVehicleSalvage from "./PartVehicleSalvage";
import { useAuth } from "../../../contexts/AuthContext";
import VehicleRecalls from "../../vehicles/VehicleRecalls";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import DepollutionButton from "../../depollution/DepollutionButton";
import EnginesWorldwideLookup from "../../vehicles/EnginesWorldwideLookup";
import VehicleStatusCell from "../../vehicles/VehicleStatusCell";
import { useLabelGenerator } from "../../../hooks/useLabelGenerator";
import printHtml from "../../../utils/printHtml";

const SingleDetail = ({
  title,
  value,
}: {
  title: string;
  value: string | null | undefined;
}) => {
  return (
    <div className="py-2 grid grid-cols-3 gap-4">
      <dt className="text-sm font-medium text-gray-500">{title}</dt>
      <dd className="text-sm text-gray-900 col-span-2">{value}</dd>
    </div>
  );
};

export default function PartVehicleDetails({
  vehicle,
  price = null,
  collectionQuestions = false,
  showDepollute = true,
}: {
  vehicle: IVehicle;
  price?: number | null;
  collectionQuestions?: boolean;
  showDepollute?: boolean;
}) {
  const { t } = useTranslation("leads");
  const navigate = useNavigate();
  const { lead } = useLeadByVehicleId("PartVehicleDetails", vehicle.id);
  const { getStockNumber } = useStockNumber();
  const { vrmRefresh, update } = useVehiclesMutations();
  const { identifierType, isVinMode } = useVehicleLookupMode();

  const { saveHandlers } = useDefaultCRUDHandlers(t("Vehicle"));
  const { addNotification } = useNotification();
  const [open, setOpen] = useState(false);
  const [exportsOpen, setExportsOpen] = useState(false);
  const [exportValuesOpen, setExportValuesOpen] = useState(false);
  const [vrm, setVrm] = useState(vehicleIdentifierFor(vehicle, identifierType));
  const [ewwEngineOffer, setEwwEngineOffer] = useState(vehicle.enginesWorldwideEngineOffer ?? "");
  const [ewwVfeOffer, setEwwVfeOffer] = useState(vehicle.enginesWorldwideVfeOffer ?? "");
  const [vlaEngineOffer, setVlaEngineOffer] = useState(vehicle.vlaEngineOffer ?? "");
  const [vlaVfeOffer, setVlaVfeOffer] = useState(vehicle.vlaVfeOffer ?? "");
  const [isCardOpen, setIsCardOpen] = useState(true);
  const { loading, claims } = useAuth();
  const [justDriver, setJustDriver] = useState(false);
  const { generateVehicleLabel } = useLabelGenerator();
  const [isPrintingVehicleLabel, setIsPrintingVehicleLabel] = useState(false);

  const handlePrintVehicleLabel = async () => {
    setIsPrintingVehicleLabel(true);
    try {
      const html = await generateVehicleLabel(vehicle);
      printHtml(html);
    } catch {
      addNotification({
        variant: "error",
        primaryText: t("Unable to print vehicle label"),
      });
    } finally {
      setIsPrintingVehicleLabel(false);
    }
  };

  const handleUpdateVrm = () => {
    // Uppercase and remove whitespace; VIN mode refreshes by VIN, which is validated first.
    const identifier = normaliseIdentifier(vrm, identifierType);

    if (!isValidIdentifier(identifier, identifierType)) {
      addNotification({ variant: "error", primaryText: t(identifierValidationMessage(identifierType)) });
      return;
    }

    const refreshBy = isVinMode ? { vehicleId: vehicle.id, vin: identifier } : { vehicleId: vehicle.id, vrm: identifier };

    vrmRefresh.mutate(refreshBy, {
      onSuccess: () => {
        setOpen(false);
        saveHandlers.onSuccess();
      },
      // The API says why it refused (e.g. the registration is already on another vehicle), so show
      // that under the generic toast rather than leaving the user guessing.
      onError: (error) => {
        addNotification({
          variant: "error",
          primaryText: t("Failed to save {{name}}", { ns: "common", name: t("Vehicle").toLowerCase() }),
          secondaryText: stepSaveErrorReason(error),
        });
      },
    });
  };

  // const handleGoToLead = () => {
  //   lead.mutate(vehicle.id, {
  //     onSuccess: (lead) => {
  //       if (lead) {
  //         console.log(lead);
  //       }
  //     },
  //     onError: () => {
  //       addNotification({
  //         variant: "error",
  //         primaryText: "Failed to lookup lead",
  //       });
  //     },
  //   });
  // };

  const openExportValuesModal = () => {
    setEwwEngineOffer(vehicle.enginesWorldwideEngineOffer ?? "");
    setEwwVfeOffer(vehicle.enginesWorldwideVfeOffer ?? "");
    setVlaEngineOffer(vehicle.vlaEngineOffer ?? "");
    setVlaVfeOffer(vehicle.vlaVfeOffer ?? "");
    setExportValuesOpen(true);
  };

  const handleUpdateExportValues = () => {
    const updatedVehicle: IVehicle = {
      ...vehicle,
      enginesWorldwideEngineOffer: ewwEngineOffer || null,
      enginesWorldwideVfeOffer: ewwVfeOffer || null,
      vlaEngineOffer: vlaEngineOffer || null,
      vlaVfeOffer: vlaVfeOffer || null,
    };

    update.mutate(updatedVehicle, {
      onSuccess: () => {
        setExportValuesOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  useEffect(() => {
    if (loading) return;

    if (
      !claims?.isAdmin &&
      !claims?.isExporter &&
      !claims?.isMechanic &&
      !claims?.isStaff &&
      claims?.isDriver
    ) {
      setJustDriver(true);
    } else {
      setJustDriver(false);
    }
  }, [loading, claims]);

  return (
    <>
      <Modal open={exportsOpen} width={"max-w-2xl"} setOpen={setExportsOpen}>
        <ExportOffersTableRow vehicle={vehicle} />
      </Modal>
      <Modal open={open} setOpen={setOpen} width="max-w-xl">
        <label>{t(isVinMode ? "Enter VIN to search for:" : "Enter VRM to search for:")}</label>
        <input
          defaultValue={vehicleIdentifierFor(vehicle, identifierType)}
          onChange={(e) => setVrm(e.target.value)}
          className="my-5 w-full text-sm uppercase"
          type="text"
          maxLength={identifierMaxLength(identifierType)}
        />
        <button
          onClick={() => handleUpdateVrm()}
          disabled={vrmRefresh.isLoading}
          className="float-right rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700 disabled:opacity-50"
        >
          {vrmRefresh.isLoading ? t("Saving...") : t("Save")}
        </button>
      </Modal>
      <Modal open={exportValuesOpen} setOpen={setExportValuesOpen} width="max-w-xl">
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">{t("Edit Export Values")}</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("EWW Engine Offer")}</label>
            <input
              value={ewwEngineOffer}
              onChange={(e) => setEwwEngineOffer(e.target.value)}
              className="mt-1 w-full text-sm"
              type="number"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("EWW VFE Offer")}</label>
            <input
              value={ewwVfeOffer}
              onChange={(e) => setEwwVfeOffer(e.target.value)}
              className="mt-1 w-full text-sm"
              type="number"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("VLA Engine Offer")}</label>
            <input
              value={vlaEngineOffer}
              onChange={(e) => setVlaEngineOffer(e.target.value)}
              className="mt-1 w-full text-sm"
              type="number"
              step="0.01"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t("VLA VFE Offer")}</label>
            <input
              value={vlaVfeOffer}
              onChange={(e) => setVlaVfeOffer(e.target.value)}
              className="mt-1 w-full text-sm"
              type="number"
              step="0.01"
            />
          </div>
          <button
            onClick={handleUpdateExportValues}
            className="float-right rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
          >
            {t("Save")}
          </button>
        </div>
      </Modal>


      <div className="relative">
        <div className="bg-white shadow rounded-lg">
          <div
            className="px-4 py-5 sm:px-6 cursor-pointer select-none"
            onClick={() => setIsCardOpen(!isCardOpen)}
          >
            <h2 className="flex justify-between items-center text-lg font-medium leading-6 text-gray-900">
              <span>{t("Vehicle Details")}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/vehicles/${vehicle.id}/edit`);
                  }}
                  className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                  title={t("Edit Vehicle")}
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <ChevronDownIcon
                  className={`h-6 w-6 text-gray-400 hover:text-gray-500 transition-transform duration-200 ease-in-out ${isCardOpen ? '' : 'rotate-180'}`}
                />
              </div>
            </h2>
          </div>
          <div className={`transition-all duration-300 ease-in-out border-t border-gray-200 ${isCardOpen ? 'max-h-full opacity-100 p-4' : 'max-h-0 opacity-0 pointer-events-none'}`}>
            {collectionQuestions && (
              <>
                <VehicleCollectionQuestions vehicle={vehicle} />
                <hr className="my-4" />
              </>
            )}

            {!vehicle?.found && (
              <div className="mb-4">
                <p className="text-red-400 font-medium text-xl text-center">
                  {t("VEHICLE NOT FOUND")}
                </p>

                <div className="flex justify-center w-full mt-2">
                  <button
                    onClick={() => setOpen(true)}
                    className="rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
                  >
                    {t("Find Vehicle")}
                  </button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-8 ">
              <div className="col-span-1 flex flex-col justify-center items-center">
                <div className="flex justify-center relative">
                  {vehicle?.vehicleImageUrl ? (
                    <>
                      <img
                        className="w-40 h-28 object-contain"
                        src={`${vehicle != null ? vehicle.vehicleImageUrl : null}`}
                      />
                    </>
                  ) : (
                    <CameraIcon className="w-14 text-gray-600" />
                  )}
                </div>
                <div>
                  <div className="flex flex-col">
                    <SingleDetail
                      title={t("Price")}
                      value={price ? PriceFormat(price) : t("N/A")}
                    />
                    <SingleDetail
                      title={t("VAT on purchase")}
                      value={vehicle?.applyVAT ? t("Yes") : t("No")}
                    />

                    {price ? <VehicleIcons vehicle={vehicle} hover /> : null}
                  </div>
                </div>
              </div>

              {/* <div className="col-span-1 flex justify-center items-center">
          <div className="flex flex-col">
            <SingleDetail title={"Price"} value={price ? PriceFormat(price) : "N/A"} />
            {price && (
              <VehicleIcons vehicle={vehicle} hover />
            )}
          </div>
        </div> */}

              <dl className="grid col-span-1 lg:col-span-7 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 divide-gray-200 items-center">
                <div className="py-2 grid grid-cols-3 gap-4 items-center">
                  <dt className="text-sm font-medium text-gray-500">{t("Status")}</dt>
                  <dd className="col-span-2">
                    <div className="w-fit">
                      <VehicleStatusCell status={vehicle.status} statusDate={vehicle.statusDate} />
                    </div>
                  </dd>
                </div>
                <SingleDetail
                  title={t("Make/Model")}
                  value={`${vehicle?.combinedMake} ${vehicle?.combinedModel}`}
                />
                {vehicle?.stockNumber && (
                  <SingleDetail title={t("Stock Number")} value={getStockNumber(vehicle)} />
                )}
                {/* <SingleDetail title={"VRM"} value={vehicle?.vrm} /> */}
                <div className="py-2 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">{t("VRM")}</dt>
                  <dd className="flex justify-between items-center text-sm text-gray-900 col-span-2">
                    <div>{vehicle.vrm}</div>
                    {!loading && !justDriver && (
                      <button
                        onClick={() => setOpen(true)}
                        className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-1 py-1 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700 mr-3"
                      >
                        {t("Update")}
                      </button>
                    )}
                  </dd>
                </div>
                <SingleDetail
                  title={t("Year")}
                  value={vehicle?.dvlaYearOfManufacture?.toString()}
                />
                <SingleDetail
                  title={t("Series")}
                  value={vehicle?.modelSeries}
                />
                <SingleDetail
                  title={t("Engine Size")}
                  value={t("{{value}}cc", { value: vehicle?.combinedEngineCapacity })}
                />
                <SingleDetail
                  title={t("Body Type")}
                  value={t("{{bodyStyle}} - {{doors}} door", { bodyStyle: vehicle?.bodyStyleDescription, doors: vehicle.numberOfDoors })}
                />
                <SingleDetail
                  title={t("Fuel Type")}
                  value={vehicle?.combinedFuelType}
                />
                <SingleDetail
                  title={t("Colour Code")}
                  value={vehicle?.vinvisColourCode}
                />
                <SingleDetail
                  title={t("Transmission Code")}
                  value={vehicle?.vinvisTransmissionCode}
                />
                <SingleDetail
                  title={t("Transmission")}
                  value={t("{{gears}} Speed {{transmission}}", { gears: vehicle?.combinedForwardGears, transmission: vehicle?.combinedTransmission })}
                />
                <SingleDetail
                  title={t("Engine Code")}
                  value={vehicle?.engineModelCode}
                />
                <SingleDetail title={t("Chassis No")} value={vehicle?.combinedVIN} />
                {vehicle?.insuranceCategory && (
                  <SingleDetail
                    title={t("Insurance Category")}
                    value={vehicle.insuranceCategory}
                  />
                )}
                <SingleDetail
                  title={t("Mileage")}
                  value={t("{{value}} miles", { value: vehicle?.odometerInMiles })}
                />
                <SingleDetail
                  title={t("MOT Expiry")}
                  value={formatDateTime(
                    vehicle?.motExpiryDate,
                    DateTime.DATE_SHORT
                  )}
                />
                <SingleDetail
                  title={t("Year Range")}
                  value={`${formatDateTime(vehicle.visibilityDate, {
                    year: "numeric",
                  })} - ${formatDateTime(vehicle.terminateDate, {
                    year: "numeric",
                  })}`}
                />
                <SingleDetail
                  title={t("Kerb Weight")}
                  value={
                    vehicle.kerbWeightMin
                      ? t("{{value}} kg", {
                        value: `${vehicle.kerbWeightMin}${vehicle.kerbWeightMax
                          ? ` - ${vehicle.kerbWeightMax}`
                          : ""
                        }`,
                      })
                      : t("Unavailable")
                  }
                />
                <SingleDetail
                  title={t("Dimensions")}
                  value={t("{{length}} x {{width}} x {{height}} m (L x W x H)", {
                    length: vehicle.lengthMM ? (vehicle.lengthMM / 1000).toFixed(2) : "0.00",
                    width: vehicle.widthMM ? (vehicle.widthMM / 1000).toFixed(2) : "0.00",
                    height: vehicle.heightMM ? (vehicle.heightMM / 1000).toFixed(2) : "0.00",
                  })}
                />
                <PartVehicleSalvage vehicle={vehicle} />
                {/* Only newer than 2011 */}
                <SingleDetail
                  title={t("Catalytic Converter (Min-Avg-Max)")}
                  value={
                    vehicle.catalyticResults !== null &&
                      vehicle.catalyticResults.length > 0
                      ? `${PriceFormat(Math.min(
                        ...vehicle.catalyticResults.map((x) => x.value)
                      ))} - ${PriceFormat(vehicle.catalyticAveragePrice)} - ${PriceFormat(Math.max(
                        ...vehicle.catalyticResults.map((x) => x.value)
                      ))}`
                      : t("Unavailable")
                  }
                />
                <div className="flex flex-col">
                  <div onClick={openExportValuesModal} className="hover:bg-gray-300 cursor-pointer rounded-md">
                    <SingleDetail
                      title={t("Export Values")}
                      value={t("VLA Engine: {{engine}} - VLA VFE: {{vfe}}", { engine: PriceFormat(vehicle.vlaEngineOffer), vfe: PriceFormat(vehicle.vlaVfeOffer) })}
                    />
                  </div>
                  <EnginesWorldwideLookup vehicle={vehicle} variant="inline" />
                </div>
                <div onClick={() => setExportsOpen(true)} className="hover:bg-gray-300 cursor-pointer rounded-md">
                  <SingleDetail
                    title={t("Export Offers")}
                    value={vehicle.exportOffers?.length > 0 ? t("{{count}} Offers", { count: vehicle.exportOffers.length }) : t("No Offers")}
                  />
                </div>

                <SingleDetail
                  title={t("Collected On")}
                  value={`${vehicle.collectedOn ? formatDateTime(
                    vehicle.collectedOn,
                    vehicle.collectedOn.includes("T") ? DateTime.DATETIME_SHORT : DateTime.DATE_SHORT
                  ) : t("Not Collected")}`}
                />
                <div className="flex items-center gap-2">

                  {showDepollute && (
                    <DepollutionButton vehicleId={vehicle.id} depollutionId={vehicle.depollutionId} openInNewTab />
                  )}
                  <ButtonInput
                    label={t("Print Vehicle Label")}
                    isSubmit={false}
                    onClick={handlePrintVehicleLabel}
                    isLoading={isPrintingVehicleLabel}
                    classes=""
                  />
                </div>

                {/* <div className="py-2 grid grid-cols-3 gap-4">
            <dt className="text-sm font-medium text-gray-500">Starts and Drives?</dt>
            <dd className="text-sm text-gray-900 col-span-2">
              {vehicle?.vehicleStarting ? "Yes" : "No"}
            </dd>
          </div> */}
              </dl>
            </div>
          </div>

          <div className="px-4">
            {vehicle?.found && <VehicleRecalls vehicle={vehicle} />}
          </div>
        </div>
      </div>
    </>
  );
}
